import { usePreferences } from './preferences';
import type { UserPreferences } from './meApi';
import { useAuthStore } from '@/features/auth/store';

/**
 * The one place a date, a time or a number becomes text for a person to read.
 *
 * ─── THE DISTINCTION THIS FILE EXISTS TO PROTECT ───────────────────────────────────────────
 *
 * A hearing is an INSTANT. It is stored as one, and nothing here ever changes it. What differs
 * is the clock it is read against:
 *
 *   PRACTICE TIME  — what the appointment MEANS. A 10:30 listing at the Rolls Building is
 *                    10:30 in the practice's timezone, for everyone, for ever. It is a fact
 *                    about the court, not about the reader.
 *
 *   VIEWER TIME    — what that same instant looks like on the clock of the person reading it.
 *                    A clerk covering from another country sees their own wall clock.
 *
 * `appearance.timezone` is a PERSONAL preference and may only influence VIEWER time. It must
 * never touch practice time, because a clerk changing their own display setting would otherwise
 * appear to move a hearing — and two people would be looking at the same listing and reading
 * different appointments. `chambers.timezone` is the organisation's and is not settable here.
 *
 * The server already resolves the inheritance (`personal ?? chambers ?? Europe/London`) and
 * hands back `effective.timezone`. That means a person who has NOT set a personal timezone sees
 * practice time everywhere, which is why turning this on changes nothing for the ordinary case.
 *
 * Use `formatDate`/`formatTime`/`formatDateTime` for anything a person reads.
 * Use `inPracticeZone` ONLY where the product deliberately shows practice time regardless of
 * who is looking, and say why at the call site.
 */

export type DateFormat = 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd';
export type TimeFormat = '24h' | '12h';

/** Just the parts of a person's preferences that decide how something is rendered. */
export interface DisplayPreferences {
  timezone: string;
  locale: string;
  dateFormat: string;
  timeFormat: string;
}

export function displayPreferencesOf(prefs: UserPreferences): DisplayPreferences {
  return {
    timezone: prefs.effective.timezone,
    locale: prefs.effective.locale,
    dateFormat: prefs.dateFormat,
    timeFormat: prefs.timeFormat,
  };
}

/** Anything a date can arrive as. Invalid input yields an em dash, never "Invalid Date". */
export type DateInput = Date | string | number | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * A timezone the browser will accept.
 *
 * A stored preference can be stale or wrong — a timezone that was removed from the IANA
 * database, or a value written before validation existed. `Intl` throws a RangeError on those,
 * which would take down whichever page happened to render a date first.
 */
function safeZone(timezone: string): string {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: timezone });
    return timezone;
  } catch {
    return 'Europe/London';
  }
}

function safeLocale(locale: string): string {
  try {
    new Intl.DateTimeFormat(locale);
    return locale;
  } catch {
    return 'en-GB';
  }
}

/** The date parts of an instant, as read in a particular zone. */
function partsIn(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: safeZone(timezone),
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // Intl renders midnight as "24" in some engines under hour12:false.
    hour: get('hour') === '24' ? '00' : get('hour'),
    minute: get('minute'),
  };
}

/**
 * The date, written the way this person asked for it.
 *
 * The pattern is applied literally rather than handed to `Intl`'s locale defaults: a person who
 * chose `yyyy-MM-dd` gets `yyyy-MM-dd` whatever their locale would otherwise impose. The locale
 * still decides month and weekday NAMES where a long form is asked for.
 */
export function formatDate(
  value: DateInput,
  prefs: DisplayPreferences,
  options?: { style?: 'numeric' | 'medium' | 'long'; timezone?: string },
): string {
  const date = toDate(value);
  if (!date) return '—';
  const zone = safeZone(options?.timezone ?? prefs.timezone);
  const style = options?.style ?? 'numeric';

  if (style !== 'numeric') {
    return new Intl.DateTimeFormat(safeLocale(prefs.locale), {
      timeZone: zone,
      day: 'numeric',
      month: style === 'long' ? 'long' : 'short',
      year: 'numeric',
    }).format(date);
  }

  const { year, month, day } = partsIn(date, zone);
  switch (prefs.dateFormat) {
    case 'MM/dd/yyyy': return `${month}/${day}/${year}`;
    case 'yyyy-MM-dd': return `${year}-${month}-${day}`;
    case 'dd/MM/yyyy':
    default:           return `${day}/${month}/${year}`;
  }
}

/** The time of day, on this person's clock and in their chosen notation. */
export function formatTime(
  value: DateInput,
  prefs: DisplayPreferences,
  options?: { timezone?: string },
): string {
  const date = toDate(value);
  if (!date) return '—';
  const zone = safeZone(options?.timezone ?? prefs.timezone);
  const { hour, minute } = partsIn(date, zone);

  if (prefs.timeFormat === '12h') {
    const h = Number(hour);
    const suffix = h < 12 ? 'am' : 'pm';
    const twelve = h % 12 === 0 ? 12 : h % 12;
    return `${twelve}:${minute}${suffix}`;
  }
  return `${hour}:${minute}`;
}

/**
 * A WALL-CLOCK time that is not an instant — "10:30" as typed, on the practice's clock.
 *
 * The diary stores a hearing as a practice date plus a wall-clock time, because that is what the
 * listing IS: a 10:30 hearing is at 10:30 whoever is reading. Converting it into a viewer's
 * timezone would be wrong — it would move the appointment on screen.
 *
 * The person's NOTATION still applies, because 24-hour versus 12-hour is a preference about
 * writing, not about which moment is meant. So this honours `timeFormat` and ignores `timezone`,
 * which is exactly the boundary the rest of this file exists to hold.
 */
export function formatWallTime(value: string | null | undefined, prefs: DisplayPreferences): string {
  if (!value) return '—';
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return value;
  const hour = Number(m[1]);
  const minute = m[2]!;
  if (Number.isNaN(hour) || hour > 23) return value;

  if (prefs.timeFormat === '12h') {
    const suffix = hour < 12 ? 'am' : 'pm';
    const twelve = hour % 12 === 0 ? 12 : hour % 12;
    return `${twelve}:${minute}${suffix}`;
  }
  return `${String(hour).padStart(2, '0')}:${minute}`;
}

export function formatDateTime(
  value: DateInput,
  prefs: DisplayPreferences,
  options?: { style?: 'numeric' | 'medium' | 'long'; timezone?: string },
): string {
  const date = toDate(value);
  if (!date) return '—';
  return `${formatDate(date, prefs, options)}, ${formatTime(date, prefs, options)}`;
}

/**
 * Format an instant in the PRACTICE's timezone, whoever is reading.
 *
 * For the few places where the number on screen is a fact about the court rather than about the
 * reader. Every call site must say why it is not viewer-local.
 */
export function inPracticeZone(prefs: DisplayPreferences, practiceTimezone: string): DisplayPreferences {
  return { ...prefs, timezone: practiceTimezone };
}

/** A number, in this person's locale. */
export function formatNumber(value: number | null | undefined, prefs: DisplayPreferences, options?: Intl.NumberFormatOptions): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(safeLocale(prefs.locale), options).format(value);
}

/**
 * Money, in this person's locale but the PRACTICE's currency.
 *
 * The locale decides where the separators go; it must never decide the currency. A barrister
 * reading their fees in a different locale is still owed pounds.
 */
export function formatCurrency(
  value: number | null | undefined,
  prefs: DisplayPreferences,
  currency = 'GBP',
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(safeLocale(prefs.locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** `yyyy-MM-dd` for an instant as it falls in a given zone — for grouping, keys and comparisons. */
export function dateKeyIn(value: DateInput, timezone: string): string | null {
  const date = toDate(value);
  if (!date) return null;
  const { year, month, day } = partsIn(date, timezone);
  return `${year}-${month}-${day}`;
}

/**
 * The PRACTICE's timezone — what a listing time means, for everybody.
 *
 * Admin-set on the chambers and read-only to members. It is deliberately NOT the viewer's:
 * a personal display preference must never be able to change what an appointment IS.
 */
export function usePracticeTimezone(): string {
  return useAuthStore((s) => s.chambers?.timezone) ?? 'Europe/London';
}

/**
 * Month and year only — "October 2026".
 *
 * For a view whose unit is the MONTH, where a day number means nothing. The month diary used
 * `formatDate(..., 'long')`, which always includes a day, so it labelled the whole of October
 * "1 October 2026" — a date the view does not represent.
 */
export function formatMonthYear(
  value: DateInput,
  prefs: DisplayPreferences,
  options?: { timezone?: string },
): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(safeLocale(prefs.locale), {
    timeZone: safeZone(options?.timezone ?? prefs.timezone),
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Formatters bound to the signed-in person's preferences.
 *
 * The hook every component should use. It re-renders when the preference changes, which is what
 * makes a saved setting visible immediately rather than at the next sign-in.
 */
export function useFormat() {
  const prefs = usePreferences();
  const display = displayPreferencesOf(prefs);
  return {
    preferences: display,
    formatDate: (v: DateInput, o?: { style?: 'numeric' | 'medium' | 'long'; timezone?: string }) => formatDate(v, display, o),
    formatTime: (v: DateInput, o?: { timezone?: string }) => formatTime(v, display, o),
    formatDateTime: (v: DateInput, o?: { style?: 'numeric' | 'medium' | 'long'; timezone?: string }) => formatDateTime(v, display, o),
    /** For a stored wall-clock "HH:MM" — notation only, never a timezone conversion. */
    formatWallTime: (v: string | null | undefined) => formatWallTime(v, display),
    /** "October 2026" — for views whose unit is a month, not a day. */
    formatMonthYear: (v: DateInput, o?: { timezone?: string }) => formatMonthYear(v, display, o),
    formatNumber: (v: number | null | undefined, o?: Intl.NumberFormatOptions) => formatNumber(v, display, o),
    formatCurrency: (v: number | null | undefined, currency?: string) => formatCurrency(v, display, currency),
    /** `yyyy-MM-dd` for "today" on this person's clock. */
    todayKey: () => dateKeyIn(new Date(), display.timezone)!,
  };
}
